import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, mutate, uploadFile } from '../shared/api';
import { formatDateTime } from '../shared/formatting';
import { ProgressOverlay, type ProgressOverlayState } from '../shared/urlState';

type Locale = 'hr' | 'en';

type Batch = {
    id: number;
    import_type: string;
    source: string | null;
    filename: string | null;
    file_hash: string | null;
    target_table: string | null;
    ingest_inbox_id: number | null;
    status: string;
    rows_total: number;
    rows_committed: number;
    rows_skipped: number;
    rows_failed: number;
    error: string | null;
    notes: string | null;
    started_at: string | null;
    completed_at: string | null;
    created_at: string;
    company_code: string | null;
    company_name: string | null;
    created_by_name: string | null;
};

type StatsByStatus = { status: string; cnt: number; rows_total: number };
type StatsByType = { import_type: string; cnt: number; rows_total: number; last_run: string | null };

type Payload = {
    rows: Batch[];
    stats_by_status: StatsByStatus[];
    stats_by_type: StatsByType[];
    companies: Array<{ id: number; code: string; name: string }>;
    distinct_types: string[];
};

type EmailLogRow = {
    id: number;
    sender_email: string | null;
    sender_name: string | null;
    subject: string | null;
    filename: string;
    size_bytes: number | null;
    classifier_guess: string | null;
    classifier_confidence: number | null;
    status: string;
    routed_to_module: string | null;
    imported_batch_id: number | null;
    import_status: string | null;
    import_error: string | null;
    created_at: string;
    company_code: string | null;
};

type DetailPayload = {
    batch: Batch;
    files: Array<{ id: number; original_name: string; mime_type: string | null; size_bytes: number | null; source_file_hash: string | null; created_at: string }>;
    sample_rows: Array<{ id: number; file_id: number; status: string | null; warnings: string | null; created_at: string }>;
};

export function AdminImportAuditSection({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<Payload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({ type: '', status: '', company_id: 0 });
    const [drawer, setDrawer] = useState<DetailPayload | null>(null);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [uploadBusy, setUploadBusy] = useState(false);
    const [uploadResult, setUploadResult] = useState<string>('');
    const [uploadOverlay, setUploadOverlay] = useState<ProgressOverlayState | null>(null);
    const [uploadIsError, setUploadIsError] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileRef = useRef<HTMLInputElement | null>(null);
    const [fetchingEmail, setFetchingEmail] = useState(false);
    const [emailFetchResult, setEmailFetchResult] = useState<{ ok: boolean; output: string } | null>(null);
    const [emailLog, setEmailLog] = useState<EmailLogRow[]>([]);
    const [showEmailLog, setShowEmailLog] = useState(false);

    const loadEmailLog = useCallback(async () => {
        try {
            const r = await api<{ rows: EmailLogRow[]; total: number }>('/admin/import-audit/email-log?limit=30', token);
            setEmailLog(r.rows);
        } catch (e) {
            // tiho — log nije kritičan
        }
    }, [token]);

    useEffect(() => { void loadEmailLog(); }, [loadEmailLog]);

    async function fetchEmail() {
        setFetchingEmail(true);
        setEmailFetchResult(null);
        try {
            const r = await api<{ ok: boolean; exit_code: number; output: string }>('/admin/import-audit/fetch-email', token, { method: 'POST' });
            setEmailFetchResult({ ok: r.ok, output: r.output });
            await load();
            await loadEmailLog();
        } catch (e) {
            setEmailFetchResult({ ok: false, output: e instanceof Error ? e.message : 'fail' });
        } finally {
            setFetchingEmail(false);
        }
    }

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const qs = new URLSearchParams();
            if (filters.type) qs.set('type', filters.type);
            if (filters.status) qs.set('status', filters.status);
            if (filters.company_id) qs.set('company_id', String(filters.company_id));
            setData(await api<Payload>(`/admin/import-audit/batches?${qs.toString()}`, token));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token, filters]);

    useEffect(() => { void load(); }, [load]);

    async function openDrawer(id: number) {
        setDrawerLoading(true);
        try {
            setDrawer(await api<DetailPayload>(`/admin/import-audit/batches/${id}`, token));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setDrawerLoading(false);
        }
    }

    async function doUploadOne(file: File): Promise<{ name: string; ok: boolean; message: string }> {
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await uploadFile<{
                duplicate?: boolean;
                inbox_id?: number;
                batch_id?: number;
                classifier?: { module_label: string | null; company_code: string | null; confidence: number };
                status?: string;
                import?: { ok: boolean; batch_id: number | null; error: string | null } | null;
                message: string;
            }>('/admin/import-audit/upload', token, fd);
            return { name: file.name, ok: true, message: r.message };
        } catch (e) {
            return { name: file.name, ok: false, message: e instanceof Error ? e.message : String(e) };
        }
    }

    async function doUploadMany(files: FileList | File[]) {
        const list = Array.from(files);
        if (list.length === 0) return;
        setUploadBusy(true);
        setUploadResult('');
        setUploadIsError(false);
        setUploadOverlay({
            label: t(`Upload ${list.length} fajlova`, `Upload ${list.length} files`),
            progress: 0,
            phase: 'preparing',
            message: t('Pripremam…', 'Preparing…'),
        });
        const results: { name: string; ok: boolean; message: string }[] = [];
        for (let i = 0; i < list.length; i++) {
            const f = list[i];
            const baseProgress = Math.round((i / list.length) * 100);
            setUploadOverlay({
                label: t(`Upload ${i + 1}/${list.length}: ${f.name}`, `Upload ${i + 1}/${list.length}: ${f.name}`),
                progress: baseProgress,
                phase: 'downloading',
                message: t('Šaljem na server + auto-klasifikator…', 'Uploading + auto-classifying…'),
            });
            setUploadResult(t(`Obrađujem ${i + 1}/${list.length}: ${f.name}…`, `Processing ${i + 1}/${list.length}: ${f.name}…`));
            const r = await doUploadOne(f);
            results.push(r);
            const afterProgress = Math.round(((i + 1) / list.length) * 100);
            setUploadOverlay((s) => s ? { ...s, progress: afterProgress, message: r.ok ? t('✓ Obrađeno', '✓ Done') : t('✗ Greška', '✗ Error') } : s);
        }
        const okCount = results.filter((r) => r.ok).length;
        const failCount = results.length - okCount;
        const summary = t(`${okCount}/${results.length} OK${failCount > 0 ? `, ${failCount} grešaka` : ''}`, `${okCount}/${results.length} OK${failCount > 0 ? `, ${failCount} errors` : ''}`);
        const details = results.map((r) => `${r.ok ? '✓' : '✗'} ${r.name}: ${r.message}`).join('\n');
        setUploadResult(`${summary}\n\n${details}`);
        setUploadIsError(failCount > 0);
        setUploadOverlay({
            label: summary,
            progress: 100,
            phase: failCount > 0 ? 'error' : 'done',
            message: failCount > 0 ? t('Završeno s greškama — vidi detalje ispod.', 'Done with errors — see details below.') : t('Sve OK ✓', 'All OK ✓'),
        });
        setTimeout(() => setUploadOverlay(null), failCount > 0 ? 2500 : 1200);
        setUploadBusy(false);
        await load();
        await loadEmailLog();
    }

    async function undoBatch(id: number) {
        const confirm = window.prompt(t(`⚠ Brišeš sve redove uvezene u batch #${id}. Za potvrdu, upiši broj batch-a:`, `⚠ Deleting all rows imported in batch #${id}. To confirm, enter the batch ID:`));
        if (confirm === null) return;
        if (parseInt(confirm, 10) !== id) {
            alert(t('Krivi broj — undo prekinut.', 'Wrong number — undo aborted.'));
            return;
        }
        setBusy(true);
        try {
            const r = await mutate<{ ok: boolean; deleted_rows: number }>(`/admin/import-audit/batches/${id}/undo`, token, 'POST', { confirm_batch_id: id });
            alert(t(`Obrisano ${r.deleted_rows} redaka.`, `Deleted ${r.deleted_rows} rows.`));
            setDrawer(null);
            await load();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setBusy(false);
        }
    }

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">📊</div>
                <div className="bi-module-hero-text">
                    <h1>172 · {t('Praćenje importa', 'Import audit')}</h1>
                    <p className="muted">
                        {t(
                            'Unified audit log za sve importe (Faros xlsx, PDF, IRA, URA, GL, bank statements). Svaki import dobija batch s file_hash dedup-om — isti fajl ne može se uvezti dvaput.',
                            'Unified audit log for all imports. Each import gets a batch with file_hash dedup — same file cannot be imported twice.',
                        )}
                    </p>
                </div>
            </div>

            {error ? <p className="error">{error}</p> : null}

            {/* Action cards row: Upload + Dohvati email + Email log toggle */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 16 }}>
                {/* Upload PDF/xlsx card — klikljiva drag & drop zona */}
                <button
                    type="button"
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); const fs = e.dataTransfer.files; if (fs && fs.length > 0) void doUploadMany(fs); }}
                    onClick={() => fileRef.current?.click()}
                    disabled={uploadBusy}
                    style={{
                        textAlign: 'left',
                        background: dragOver
                            ? 'rgba(var(--ad-acc-rgb),0.10)'
                            : 'linear-gradient(135deg, #fffbeb 0%, rgba(254,243,199,0.4) 100%)',
                        border: dragOver ? '2.5px dashed var(--ad-acc)' : '2.5px dashed rgba(var(--ad-soft-rgb),0.45)',
                        borderRadius: 14,
                        padding: '1.1rem 1.2rem',
                        cursor: uploadBusy ? 'wait' : 'pointer',
                        transition: 'all 160ms ease',
                        opacity: uploadBusy ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => { if (! uploadBusy && ! dragOver) (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: 6 }}>
                        <span style={{ fontSize: '1.8rem' }}>{uploadBusy ? '⏳' : '📤'}</span>
                        <strong style={{ color: 'var(--ad-ink)', fontSize: '1rem' }}>
                            {uploadBusy ? t('Obrađujem…', 'Processing…') : (dragOver ? t('Otpusti ovdje (više fajlova OK)', 'Drop here (multiple files OK)') : t('Upload (povuci ili klikni — više fajlova OK)', 'Upload (drop or click — multiple files OK)'))}
                        </strong>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.4 }}>
                        {t(
                            'xlsx / PDF / CSV — jedan ili više odjednom. Sustav klasificira i automatski usmjeri u pravi modul.',
                            'xlsx / PDF / CSV — one or many at once. System classifies and auto-routes to the right module.',
                        )}
                    </p>
                    <input
                        ref={fileRef}
                        type="file"
                        multiple
                        accept=".xlsx,.xls,.pdf,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,text/csv"
                        style={{ display: 'none' }}
                        onChange={(e) => { const fs = e.target.files; if (fs && fs.length > 0) void doUploadMany(fs); e.currentTarget.value = ''; }}
                    />
                </button>

                {/* Dohvati email card */}
                <button
                    type="button"
                    disabled={fetchingEmail}
                    onClick={() => void fetchEmail()}
                    style={{
                        textAlign: 'left',
                        background: fetchingEmail
                            ? 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)'
                            : 'linear-gradient(135deg, #eff6ff 0%, rgba(219,234,254,0.55) 100%)',
                        border: '1.5px solid rgba(59,130,246,0.28)',
                        borderRadius: 14,
                        padding: '1.1rem 1.2rem',
                        cursor: fetchingEmail ? 'wait' : 'pointer',
                        transition: 'all 160ms ease',
                        opacity: fetchingEmail ? 0.7 : 1,
                    }}
                    onMouseEnter={(e) => { if (! fetchingEmail) (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: 6 }}>
                        <span style={{ fontSize: '1.8rem' }}>{fetchingEmail ? '⏳' : '📧'}</span>
                        <strong style={{ color: '#1e40af', fontSize: '1rem' }}>
                            {fetchingEmail ? t('Dohvaćam…', 'Fetching…') : t('Dohvati email (obrada@)', 'Fetch email (obrada@)')}
                        </strong>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.4 }}>
                        {t('Ručno povuci nove privitke iz obrada@aurodomus.eu. Cron radi svaku minutu.', 'Manually pull attachments from obrada@aurodomus.eu. Cron runs every minute.')}
                    </p>
                </button>

                {/* Email log card */}
                <button
                    type="button"
                    onClick={() => setShowEmailLog((v) => !v)}
                    style={{
                        textAlign: 'left',
                        background: showEmailLog
                            ? 'linear-gradient(135deg, #f0fdf4 0%, rgba(220,252,231,0.55) 100%)'
                            : 'linear-gradient(135deg, #f8fafc 0%, rgba(241,245,249,0.55) 100%)',
                        border: showEmailLog ? '1.5px solid rgba(31,122,54,0.35)' : '1.5px solid rgba(148,163,184,0.32)',
                        borderRadius: 14,
                        padding: '1.1rem 1.2rem',
                        cursor: 'pointer',
                        transition: 'all 160ms ease',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: 6 }}>
                        <span style={{ fontSize: '1.8rem' }}>📜</span>
                        <strong style={{ color: showEmailLog ? '#1f7a36' : '#334155', fontSize: '1rem', flex: 1 }}>
                            {t('Email log', 'Email log')}
                        </strong>
                        <span style={{
                            background: showEmailLog ? '#1f7a36' : '#94a3b8',
                            color: '#fff', fontSize: '0.72rem', fontWeight: 700,
                            padding: '2px 8px', borderRadius: 10, minWidth: 26, textAlign: 'center',
                        }}>{emailLog.length}</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(0,0,0,0.6)', lineHeight: 1.4 }}>
                        {t('Zadnji primljeni / upload-ani fajlovi kroz ingest pipeline.', 'Latest received / uploaded files through the ingest pipeline.')}
                    </p>
                </button>
            </div>

            {uploadResult ? (
                <div style={{
                    padding: '10px 14px',
                    marginBottom: 14,
                    borderRadius: 10,
                    background: uploadIsError ? 'rgba(177,64,64,0.08)' : 'rgba(31,122,54,0.08)',
                    border: uploadIsError ? '1px solid rgba(177,64,64,0.3)' : '1px solid rgba(31,122,54,0.3)',
                    fontSize: '0.86rem',
                    color: uploadIsError ? '#b14040' : '#1f7a36',
                    whiteSpace: 'pre-wrap',
                    fontFamily: uploadResult.includes('\n') ? 'ui-monospace, SFMono-Regular, monospace' : undefined,
                }}>
                    {uploadResult}
                </div>
            ) : null}

            {emailFetchResult ? (
                <div style={{
                    padding: '10px 14px',
                    marginBottom: 14,
                    borderRadius: 10,
                    background: emailFetchResult.ok ? 'rgba(31,122,54,0.08)' : 'rgba(177,64,64,0.08)',
                    border: emailFetchResult.ok ? '1px solid rgba(31,122,54,0.3)' : '1px solid rgba(177,64,64,0.3)',
                    fontSize: '0.84rem',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <strong style={{ color: emailFetchResult.ok ? '#1f7a36' : '#b14040' }}>
                            {emailFetchResult.ok ? '✓' : '✗'} {t('Email fetch', 'Email fetch')}
                        </strong>
                        <button type="button" onClick={() => setEmailFetchResult(null)} style={{ marginLeft: 'auto', background: 'transparent', border: 0, cursor: 'pointer' }}>×</button>
                    </div>
                    {emailFetchResult.output ? <pre style={{ margin: '6px 0 0', fontSize: '0.74rem', whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, Menlo, monospace' }}>{emailFetchResult.output}</pre> : null}
                </div>
            ) : null}

            {showEmailLog ? (
                <div style={{ marginBottom: 16, background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.25)', borderRadius: 10, padding: '12px 14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <strong style={{ color: 'var(--ad-ink)', fontSize: '0.9rem' }}>📜 {t('Email log (zadnjih 30)', 'Email log (last 30)')}</strong>
                        <button type="button" className="secondary compact" onClick={() => void loadEmailLog()} style={{ fontSize: '0.78rem' }}>🔄 {t('Osvježi', 'Refresh')}</button>
                    </div>
                    {emailLog.length === 0 ? (
                        <p className="muted" style={{ textAlign: 'center', padding: '0.8rem', fontStyle: 'italic', fontSize: '0.82rem' }}>
                            {t('Nema primljenih fajlova.', 'No received files.')}
                        </p>
                    ) : (
                        <div className="table-wrap">
                            <table className="admin-user-table" style={{ fontSize: '0.8rem' }}>
                                <thead>
                                    <tr>
                                        <th>{t('Datum', 'Date')}</th>
                                        <th>{t('Source', 'Source')}</th>
                                        <th>{t('Fajl', 'File')}</th>
                                        <th>{t('Tip', 'Type')}</th>
                                        <th>{t('Firma', 'Co.')}</th>
                                        <th>{t('Status', 'Status')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {emailLog.map((r) => (
                                        <tr key={r.id}>
                                            <td>{formatDateTime(r.created_at, locale)}</td>
                                            <td style={{ fontSize: '0.76rem' }}>{r.sender_email === 'manual_upload' ? <span style={{ color: 'var(--ad-ink)' }}>📤 upload</span> : <span style={{ color: '#1e40af' }}>📧 {r.sender_email ?? '—'}</span>}</td>
                                            <td title={r.filename}>{r.filename.length > 36 ? r.filename.slice(0, 36) + '…' : r.filename}</td>
                                            <td className="muted" style={{ fontSize: '0.76rem' }}>{r.classifier_guess ?? '—'}</td>
                                            <td>{r.company_code ?? <span className="muted">—</span>}</td>
                                            <td>
                                                <span style={{
                                                    background: r.import_status === 'success' ? 'rgba(31,122,54,0.15)'
                                                        : (r.import_status === 'failed' ? 'rgba(177,64,64,0.15)'
                                                        : (r.status === 'pending' ? 'rgba(var(--ad-acc-rgb),0.15)'
                                                        : 'rgba(148,163,184,0.15)')),
                                                    color: r.import_status === 'success' ? '#1f7a36'
                                                        : (r.import_status === 'failed' ? '#b14040'
                                                        : (r.status === 'pending' ? 'var(--ad-acc-3)' : '#475569')),
                                                    padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700,
                                                }}>
                                                    {r.import_status === 'success' ? `✓ batch #${r.imported_batch_id}` :
                                                     r.import_status === 'failed' ? '✗ failed' :
                                                     r.import_status === 'pending' ? '⏳ in progress' :
                                                     r.status === 'pending' ? '⏳ čeka admin' :
                                                     r.status === 'auto_routed' ? 'auto' :
                                                     r.status === 'routed' ? 'routed' :
                                                     r.status === 'rejected' ? 'rejected' : r.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ) : null}

            {data ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
                    {data.stats_by_status.map((s) => (
                        <div key={s.status} style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 10, padding: '10px 14px' }}>
                            <div className="muted" style={{ fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{s.status}</div>
                            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: s.status === 'committed' ? '#1f7a36' : (s.status === 'failed' ? '#b14040' : '#34383d') }}>
                                {s.cnt} batch-eva · {Number(s.rows_total || 0).toLocaleString('hr-HR')} redaka
                            </div>
                        </div>
                    ))}
                </div>
            ) : null}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', fontSize: '0.84rem' }}>
                    <option value="">{t('Sve vrste', 'All types')}</option>
                    {(data?.distinct_types ?? []).map((tp) => <option key={tp} value={tp}>{tp}</option>)}
                </select>
                <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', fontSize: '0.84rem' }}>
                    <option value="">{t('Svi statusi', 'All statuses')}</option>
                    <option value="committed">committed</option>
                    <option value="pending">pending</option>
                    <option value="failed">failed</option>
                    <option value="undone">undone</option>
                </select>
                <select value={filters.company_id} onChange={(e) => setFilters({ ...filters, company_id: parseInt(e.target.value, 10) })} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', fontSize: '0.84rem' }}>
                    <option value="0">{t('Sve firme', 'All companies')}</option>
                    {(data?.companies ?? []).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
                </select>
                {loading ? <span className="muted" style={{ fontSize: '0.78rem' }}>{t('Učitavam…', 'Loading…')}</span> : null}
            </div>

            {data && data.rows.length === 0 ? (
                <p className="muted" style={{ textAlign: 'center', padding: '1.5rem', fontStyle: 'italic' }}>
                    {t('Nema importa s ovim filterima.', 'No imports with these filters.')}
                </p>
            ) : null}

            <div className="table-wrap">
                <table className="admin-user-table" style={{ fontSize: '0.84rem' }}>
                    <thead>
                        <tr>
                            <th>{t('Datum', 'Date')}</th>
                            <th>{t('Vrsta', 'Type')}</th>
                            <th>{t('Firma', 'Company')}</th>
                            <th>{t('Fajl', 'File')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Total', 'Total')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Uneseno', 'Inserted')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Preskočeno', 'Skipped')}</th>
                            <th>{t('Status', 'Status')}</th>
                            <th>{t('Source', 'Source')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(data?.rows ?? []).map((b) => (
                            <tr key={b.id} onClick={() => void openDrawer(b.id)} style={{ cursor: 'pointer' }}>
                                <td>{formatDateTime(b.created_at, locale)}</td>
                                <td><strong>{b.import_type}</strong></td>
                                <td>{b.company_code ?? <span className="muted">—</span>}</td>
                                <td title={b.filename ?? ''}>{(b.filename ?? '').slice(0, 40)}{(b.filename ?? '').length > 40 ? '…' : ''}</td>
                                <td style={{ textAlign: 'right' }}>{Number(b.rows_total).toLocaleString('hr-HR')}</td>
                                <td style={{ textAlign: 'right', color: '#1f7a36' }}>{Number(b.rows_committed).toLocaleString('hr-HR')}</td>
                                <td style={{ textAlign: 'right', color: b.rows_skipped > 0 ? 'var(--ad-acc-3)' : '#999' }}>{b.rows_skipped > 0 ? Number(b.rows_skipped).toLocaleString('hr-HR') : '—'}</td>
                                <td>
                                    <span style={{
                                        background: b.status === 'committed' ? 'rgba(31,122,54,0.15)' : (b.status === 'failed' ? 'rgba(177,64,64,0.15)' : 'rgba(var(--ad-acc-rgb),0.15)'),
                                        color: b.status === 'committed' ? '#1f7a36' : (b.status === 'failed' ? '#b14040' : 'var(--ad-acc-3)'),
                                        padding: '2px 8px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700,
                                    }}>{b.status}</span>
                                </td>
                                <td className="muted" style={{ fontSize: '0.78rem' }}>{b.source ?? '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {drawer ? (
                <div className="modal-backdrop" onClick={() => setDrawer(null)} role="dialog" aria-modal="true" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <aside onClick={(e) => e.stopPropagation()} style={{
                        width: 'min(560px, 96vw)', height: '100%',
                        background: 'linear-gradient(180deg, #fffdf6 0%, var(--ad-bg) 100%)',
                        boxShadow: '-20px 0 60px rgba(107,79,28,0.22)',
                        overflowY: 'auto',
                    }}>
                        <header style={{ background: 'linear-gradient(135deg, var(--ad-ink) 0%, #8b6b2c 60%, var(--ad-acc) 100%)', color: '#fffbeb', padding: '16px 20px', display: 'flex', justifyContent: 'space-between' }}>
                            <div>
                                <p style={{ margin: 0, fontSize: '0.72rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,251,235,0.7)' }}>
                                    {t('Batch detalji', 'Batch detail')}
                                </p>
                                <h2 style={{ margin: '0.2rem 0 0', fontSize: '1.25rem' }}>#{drawer.batch.id} · {drawer.batch.import_type}</h2>
                                <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: 'rgba(255,251,235,0.78)' }}>
                                    {drawer.batch.filename ?? '—'}
                                </p>
                            </div>
                            <button type="button" onClick={() => setDrawer(null)} style={{ background: 'transparent', color: '#fffbeb', border: 0, fontSize: '1.7rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
                        </header>
                        <div style={{ padding: '16px 20px' }}>
                            <dl style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '4px 12px', fontSize: '0.86rem', margin: 0 }}>
                                <dt className="muted">{t('Status', 'Status')}</dt><dd><strong style={{ color: drawer.batch.status === 'committed' ? '#1f7a36' : (drawer.batch.status === 'failed' ? '#b14040' : 'var(--ad-acc-3)') }}>{drawer.batch.status}</strong></dd>
                                <dt className="muted">{t('Firma', 'Company')}</dt><dd>{drawer.batch.company_code ? `${drawer.batch.company_code} · ${drawer.batch.company_name}` : '—'}</dd>
                                <dt className="muted">{t('Target tablica', 'Target table')}</dt><dd><code style={{ background: 'rgba(0,0,0,0.05)', padding: '1px 6px', borderRadius: 4 }}>{drawer.batch.target_table ?? '—'}</code></dd>
                                <dt className="muted">{t('Source', 'Source')}</dt><dd>{drawer.batch.source ?? '—'}</dd>
                                <dt className="muted">{t('Uveo', 'Imported by')}</dt><dd>{drawer.batch.created_by_name ?? '—'}</dd>
                                <dt className="muted">{t('File hash', 'File hash')}</dt><dd style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.74rem' }}>{(drawer.batch.file_hash ?? '').slice(0, 32)}…</dd>
                                <dt className="muted">{t('Total / OK / Skip / Fail', 'Total / OK / Skip / Fail')}</dt><dd><strong>{drawer.batch.rows_total}</strong> / <span style={{ color: '#1f7a36' }}>{drawer.batch.rows_committed}</span> / <span style={{ color: 'var(--ad-acc-3)' }}>{drawer.batch.rows_skipped}</span> / <span style={{ color: '#b14040' }}>{drawer.batch.rows_failed}</span></dd>
                                <dt className="muted">{t('Po četeo', 'Started')}</dt><dd>{drawer.batch.started_at ? formatDateTime(drawer.batch.started_at, locale) : '—'}</dd>
                                <dt className="muted">{t('Završio', 'Completed')}</dt><dd>{drawer.batch.completed_at ? formatDateTime(drawer.batch.completed_at, locale) : '—'}</dd>
                            </dl>
                            {drawer.batch.error ? (
                                <div style={{ marginTop: 14, background: 'rgba(177,64,64,0.08)', border: '1px solid rgba(177,64,64,0.3)', borderRadius: 8, padding: '10px 12px', fontSize: '0.82rem' }}>
                                    <strong style={{ color: '#b14040' }}>{t('Greška', 'Error')}:</strong>
                                    <pre style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem' }}>{drawer.batch.error}</pre>
                                </div>
                            ) : null}
                            {drawer.batch.notes ? (
                                <p className="muted" style={{ marginTop: 12, fontSize: '0.8rem' }}>📝 {drawer.batch.notes}</p>
                            ) : null}

                            {drawer.files.length > 0 ? (
                                <>
                                    <h3 style={{ margin: '14px 0 6px', fontSize: '0.74rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ad-acc-3)' }}>{t('Fajlovi', 'Files')}</h3>
                                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '0.84rem' }}>
                                        {drawer.files.map((f) => (
                                            <li key={f.id} style={{ padding: '6px 0', borderBottom: '1px solid rgba(var(--ad-soft-rgb),0.15)' }}>
                                                📎 {f.original_name} · <span className="muted">{f.size_bytes ? `${(f.size_bytes / 1024).toFixed(1)} KB` : '—'}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : null}

                            {drawer.batch.status === 'committed' && drawer.batch.target_table ? (
                                <div style={{ marginTop: 18, borderTop: '1px solid rgba(var(--ad-soft-rgb),0.2)', paddingTop: 14 }}>
                                    <button type="button" className="compact" onClick={() => void undoBatch(drawer.batch.id)} disabled={busy} style={{ background: '#b14040', color: '#fff' }}>
                                        🗑 {t('Undo (obriši sve redove iz batch-a)', 'Undo (delete all rows in batch)')}
                                    </button>
                                    <p className="muted" style={{ fontSize: '0.74rem', marginTop: 8 }}>
                                        {t('⚠ Briše sve redove u target tablici koji imaju batch_id=#'+drawer.batch.id+'. Tražit će potvrdu broj batch-a.', '⚠ Deletes all rows in target table with batch_id=#'+drawer.batch.id+'. Will ask for batch number confirmation.')}
                                    </p>
                                </div>
                            ) : null}
                        </div>
                    </aside>
                </div>
            ) : null}
            {drawerLoading ? <p className="muted">{t('Učitavam detalje…', 'Loading details…')}</p> : null}
            <ProgressOverlay state={uploadOverlay} />
        </section>
    );
}
